import { useI18n } from '@ecom/i18n';
import { Card, EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { formatPeriod } from '../format';
import { type TrendBucket } from '../types';

const VIEW_H = 210;
const PLOT_LEFT = 44;
const PLOT_TOP = 14;
const PLOT_BOTTOM = 172;
/** Used until the real width is known (and where nothing can be measured, as in a test). */
const FALLBACK_WIDTH = 320;

/** The pixel width of an element, followed as it changes: a chart drawn at its own width never scales its text. */
function useElementWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === 'undefined') return undefined;
    const measure = (): void => {
      const next = Math.floor(element.getBoundingClientRect().width);
      if (next >= 160) setWidth(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);
  return [ref, width];
}

export interface ChartPoint {
  /** `YYYY-MM-DD` */
  readonly period: string;
  readonly value: number;
}

export interface TrendChartProps {
  readonly title: string;
  readonly points: readonly ChartPoint[];
  readonly bucket: TrendBucket;
  readonly kind: 'line' | 'bar';
  /** The reader's way of writing one value (an amount in TND, a count). */
  readonly formatValue: (value: number) => string;
  /** A short form for the vertical axis (« 12 k »). */
  readonly formatAxis: (value: number) => string;
  /** CSS colour of the series: a design token, so the chart follows light / dark. */
  readonly color: string;
  readonly testId?: string;
}

/** A round upper bound for the axis: 1, 2, 5 times a power of ten. */
export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const base = 10 ** exponent;
  const scaled = value / base;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return step * base;
}

/**
 * One curve of the dashboard, drawn in plain SVG (no charting dependency): a line with its area for a series that flows (sales), bars
 * for counts (orders, new merchants). The time axis always runs left to right, in every language - dates and digits are read that way
 * even in Arabic - so the drawing sits in an explicit `dir="ltr"` box; the title, the legend and the text around it follow the page.
 *
 * Accessible without sight: the figure is an image with a text summary (total, first and last day) AND a hidden table of every point.
 * A native tooltip (`<title>`) gives each point's value on hover. Nothing is invented: a series with no value at all says so.
 */
export function TrendChart({
  title,
  points,
  bucket,
  kind,
  formatValue,
  formatAxis,
  color,
  testId,
}: TrendChartProps): ReactElement {
  const { t, locale } = useI18n();
  const [box, viewWidth] = useElementWidth<HTMLDivElement>();
  const plotRight = viewWidth - 8;
  const total = points.reduce((sum, point) => sum + point.value, 0);
  const peak = points.reduce<ChartPoint | null>(
    (best, point) => (best === null || point.value > best.value ? point : best),
    null,
  );
  const first = points[0];
  const last = points.at(-1);

  const upper = niceMax(Math.max(...points.map((point) => point.value), 0));
  const width = plotRight - PLOT_LEFT;
  const height = PLOT_BOTTOM - PLOT_TOP;
  const yFor = (value: number): number => PLOT_BOTTOM - (value / upper) * height;
  const slot = points.length === 0 ? width : width / points.length;
  const xFor = (index: number): number =>
    kind === 'bar'
      ? PLOT_LEFT + slot * index + slot / 2
      : points.length <= 1
        ? PLOT_LEFT + width / 2
        : PLOT_LEFT + (index / (points.length - 1)) * width;

  const linePoints = points
    .map((point, index) => `${xFor(index).toFixed(1)},${yFor(point.value).toFixed(1)}`)
    .join(' ');
  const areaPoints = `${String(PLOT_LEFT)},${String(PLOT_BOTTOM)} ${linePoints} ${xFor(points.length - 1).toFixed(1)},${String(PLOT_BOTTOM)}`;
  const gridValues = [0, upper / 2, upper];
  const tickIndexes = Array.from(
    new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]),
  ).filter((index) => index >= 0);
  const label = (period: string): string => formatPeriod(period, bucket, locale);

  const ariaLabel =
    first !== undefined && last !== undefined
      ? t('admin.dashboard.trend.aria', {
          title,
          total: formatValue(total),
          from: label(first.period),
          to: label(last.period),
        })
      : title;

  return (
    <Card className="flex flex-col gap-2 p-4" data-testid={testId}>
      <h3 className="font-display text-sm font-bold text-ink">{title}</h3>

      {total === 0 ? (
        <EmptyState title={t('admin.dashboard.trend.empty')} />
      ) : (
        <>
          <div dir="ltr" ref={box}>
            <svg
              viewBox={`0 0 ${String(viewWidth)} ${String(VIEW_H)}`}
              width={viewWidth}
              height={VIEW_H}
              className="block max-w-full"
              role="img"
              aria-label={ariaLabel}
              focusable="false"
            >
              {gridValues.map((value) => (
                <g key={value}>
                  <line
                    x1={PLOT_LEFT}
                    x2={plotRight}
                    y1={yFor(value)}
                    y2={yFor(value)}
                    stroke="var(--ecom-line-soft)"
                    strokeWidth="1"
                  />
                  <text
                    x={PLOT_LEFT - 6}
                    y={yFor(value) + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="var(--ecom-ink-faint)"
                  >
                    {formatAxis(value)}
                  </text>
                </g>
              ))}

              {kind === 'line' ? (
                <>
                  <polygon points={areaPoints} fill={color} fillOpacity="0.14" />
                  <polyline
                    points={linePoints}
                    fill="none"
                    stroke={color}
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {/* A dot per day that has a value (and today): a run of zeros stays a plain line, not a dotted one. */}
                  {points.map((point, index) =>
                    point.value > 0 || index === points.length - 1 ? (
                      <circle
                        key={point.period}
                        cx={xFor(index)}
                        cy={yFor(point.value)}
                        r={index === points.length - 1 ? 4 : 2.5}
                        fill={color}
                      >
                        <title>{`${label(point.period)} : ${formatValue(point.value)}`}</title>
                      </circle>
                    ) : null,
                  )}
                </>
              ) : (
                points.map((point, index) => {
                  const barHeight = Math.max(
                    point.value > 0 ? 2 : 0,
                    PLOT_BOTTOM - yFor(point.value),
                  );
                  const barWidth = Math.max(2, slot * 0.62);
                  return (
                    <rect
                      key={point.period}
                      x={xFor(index) - barWidth / 2}
                      y={PLOT_BOTTOM - barHeight}
                      width={barWidth}
                      height={barHeight}
                      rx="2"
                      fill={color}
                    >
                      <title>{`${label(point.period)} : ${formatValue(point.value)}`}</title>
                    </rect>
                  );
                })
              )}

              {tickIndexes.map((index) => {
                const point = points[index];
                if (point === undefined) return null;
                const anchor =
                  index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle';
                return (
                  <text
                    key={point.period}
                    x={xFor(index)}
                    y={PLOT_BOTTOM + 18}
                    textAnchor={anchor}
                    fontSize="11"
                    fill="var(--ecom-ink-faint)"
                  >
                    {label(point.period)}
                  </text>
                );
              })}
            </svg>
          </div>

          {peak !== null ? (
            <p className="text-xs text-inkFaint">
              {t('admin.dashboard.trend.peak', {
                value: formatValue(peak.value),
                date: label(peak.period),
              })}
            </p>
          ) : null}

          {/* A div carries `sr-only`: the class does not clip a <table> itself, which then widens the whole page. */}
          <div className="sr-only">
            <table>
              <caption>{t('admin.dashboard.trend.tableCaption', { title })}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('admin.dashboard.trend.colPeriod')}</th>
                  <th scope="col">{title}</th>
                </tr>
              </thead>
              <tbody>
                {points.map((point) => (
                  <tr key={point.period}>
                    <th scope="row">{label(point.period)}</th>
                    <td>{formatValue(point.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
